<template>
    <div
        class="flex flex-col items-start gap-2 flex-1 px-4 py-5 sm:p-4 md:rounded-lg"
    >
        <dt class="py-1 mb-1 relative">
            <div class="flex items-center">
                <div class="text-lg font-medium text-slate-800 dark:text-slate-100">
                    5/24 status
                </div>
                <span
                    id="annual-fee-label-tooltip"
                    class="inline-block rounded-full text-slate-400 text-center relative cursor-pointer ml-1"
                    @mouseenter="showTooltip = true"
                    @mouseleave="handleMouseLeave"
                    @touchstart.prevent="toggleTooltip"
                    @click.outside="showTooltip = false"
                    ref="tooltipTrigger"
                >
                    <Icon icon="circle-info" />
                    <div
                        class="absolute bg-white dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700 p-6 shadow-lg dark:shadow-slate-900/50 md:transition-all md:duration-200 md:ease-in-out z-30 w-[320px] -right-32"
                        v-if="showTooltip"
                        ref="tooltipContent"
                    >
                        <div class="flex flex-col">
                            <div class="flex justify-center items-center mb-4">
                                <icon icon="graphic-rule" />
                            </div>
                            <h3 class="text-left text-base font-medium mb-2">
                                What is the 5/24 rule?
                            </h3>
                            <p
                                class="text-left font-normal text-sm text-slate-500 dark:text-slate-400 mb-4"
                            >
                                To be approved for a new credit card from Chase,
                                you can't have opened five or more personal
                                credit cards across all banks within the past 24
                                months. This means that you must be under five
                                personal credit cards to be approved.
                            </p>
                            <a
                                href="https://10xtravel.com/chase-5-24-rule/"
                                target="_blank"
                                class="btn-base btn-link flex items-center text-slate-800 dark:text-slate-100 text-sm font-medium rounded hover:text-slate-700 dark:hover:text-slate-300 active:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-0"
                                id="hs-dashboard-five-twenty-four-learn-more"
                            >
                                Learn more
                                <Icon icon="arrow" />
                            </a>
                        </div>
                    </div>
                </span>
            </div>
        </dt>
        <dashboard-stat-skeleton v-if="loading" />
        <template v-else>
            <dd class="mt-1 text-3xl font-medium text-slate-800 dark:text-slate-100 mb-1">
                <template v-if="count === 0">
                    <hr
                        class="w-4 border-slate-800 dark:border-slate-100 border-b-2 rounded-md text-3xl font-semibold text-slate-900 dark:text-slate-100 mb-3 mt-6"
                    />
                </template>
                <template v-else>
                    <div
                        class="flex items-center gap-2 relative"
                        ref="dropdown"
                    >
                        {{ count }}/24
                        <button
                            v-if="count > 0"
                            @click="togglePopover"
                            class="btn-base btn-link focus:outline-none"
                            ref="popoverTrigger"
                            id="hs-dashboard-five-twenty-four-detail"
                        >
                            <Icon icon="chevron-down-small" />
                        </button>
                        <Teleport to="body">
                            <div
                                v-if="isPopoverOpen"
                                ref="popoverContent"
                                :style="popoverFloatingStyles"
                                class="bg-white dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700 p-2 min-w-[300px] shadow-lg dark:shadow-slate-900/50 z-[9999]"
                            >
                                <div
                                    v-for="card in cards"
                                    :key="card.id"
                                    class="flex items-center gap-3 p-2 rounded-md card-cursor"
                                >
                                    <img
                                        :src="getCardImageUrl(card)"
                                        alt="card image"
                                        class="w-[76px] h-12 rounded-sm object-cover shadow-sm"
                                    />
                                    <div class="flex flex-col">
                                        <div
                                            class="text-sm font-medium text-gray-900 dark:text-gray-100 text-left"
                                        >
                                            {{ card.card?.title }}
                                            <span
                                                v-if="card.last_four"
                                                class="ml-1"
                                            >
                                                {{ `(${card.last_four})` }}
                                            </span>
                                        </div>
                                        <div
                                            class="text-sm text-gray-500 dark:text-gray-400 text-left"
                                        >
                                            Opened
                                            {{
                                                formatDate(card.actual_date_opened)
                                            }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Teleport>
                    </div>
                </template>
            </dd>
            <dt class="truncate text-sm font-medium text-slate-500 dark:text-slate-400">
                {{ count > 4 ? `Next under 5/24 - ${nextCardDate}` : 'Count' }}
            </dt>
        </template>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useDashboardStore } from '@/store/dashboard';
import { storeToRefs } from 'pinia';
import {
    computePosition,
    shift,
    flip,
    useFloating,
    autoUpdate,
    offset as offsetMiddleware,
} from '@floating-ui/vue';
import Icon from '@/components/ui/icon/icon.vue';
import icon from '@/components/ui/icon/icon-rule.vue';
import DashboardStatSkeleton from './dashboard-stat-skeleton.vue';

// Store setup
const dashboardStore = useDashboardStore();
const { count, nextCardDate, loading } = storeToRefs(dashboardStore);

// Refs
const cdnUrl = ref('');
const isPopoverOpen = ref(false);
const cards = ref([]);
const showTooltip = ref(false);
const tooltipTrigger = ref(null);
const tooltipContent = ref(null);
const dropdown = ref(null);
const popoverTrigger = ref(null);
const popoverContent = ref(null);

const { floatingStyles: popoverFloatingStyles } = useFloating(
    popoverTrigger,
    popoverContent,
    {
        strategy: 'fixed',
        placement: 'bottom-end',
        whileElementsMounted: autoUpdate,
        middleware: [
            offsetMiddleware(8),
            flip({ padding: 8 }),
            shift({ padding: 8 }),
        ],
    },
);

// Computed
const nextCardTitle = computed(() => {
    return 'latest';
});

// Methods
const togglePopover = async () => {
    if (!isPopoverOpen.value) {
        await loadCards();
    }
    isPopoverOpen.value = !isPopoverOpen.value;
};

const handleOutsideClick = (event) => {
    if (
        popoverContent.value &&
        !popoverContent.value.contains(event.target) &&
        popoverTrigger.value &&
        !popoverTrigger.value.contains(event.target)
    ) {
        isPopoverOpen.value = false;
    }
};

const handleTooltipClickOutside = (event) => {
    if (tooltipContent.value && !tooltipContent.value.contains(event.target)) {
        showTooltip.value = false;
    }
};

const toggleTooltip = (event) => {
    event.preventDefault();
    event.stopPropagation();
    showTooltip.value = !showTooltip.value;

    if (showTooltip.value) {
        nextTick(() => {
            updateTooltipPosition();
        });
    }
};

const loadCards = async () => {
    const allCards = await dashboardStore.getCardsForUser();
    const cardsArray = Object.values(allCards);
    cards.value = cardsArray.filter((card) => {
        return card.contributes_to_wallet_five_twenty_four;
    });
};

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const getCardImageUrl = (card) => {
    if (card.card?.card_image?.file_name) {
        return `${cdnUrl.value}/${card.card.card_image.path}/_small/${card.card.card_image.file_name}`;
    }
    return '/images/cards/default.png';
};

const handleMouseLeave = () => {
    if (!('ontouchstart' in window)) {
        showTooltip.value = false;
    }
};

const updateTooltipPosition = () => {
    if (tooltipTrigger.value && tooltipContent.value) {
        // Set initial position with CSS
        tooltipContent.value.style.position = 'absolute';
        tooltipContent.value.style.top = '100%';
        tooltipContent.value.style.left = '50%';
        tooltipContent.value.style.width = '320px';

        // Use FloatingUI just for the shift to prevent overflow
        computePosition(tooltipTrigger.value, tooltipContent.value, {
            placement: 'bottom',
            middleware: [flip({ padding: 5 }), shift({ padding: 2 })],
        }).then(({ x, y }) => {
            // Only apply the x/y if needed to prevent overflow
            tooltipContent.value.style.left = `${x}px`;
            tooltipContent.value.style.top = `${y}px`;
        });
    }
};

// Lifecycle hooks
onMounted(() => {
    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('click', handleTooltipClickOutside);
    document.addEventListener('touchstart', handleTooltipClickOutside);
    window.addEventListener('resize', handleResize);
    cdnUrl.value = window.cdnUrl;
});

onUnmounted(() => {
    document.removeEventListener('click', handleOutsideClick);
    document.removeEventListener('click', handleTooltipClickOutside);
    document.removeEventListener('touchstart', handleTooltipClickOutside);
    window.removeEventListener('resize', handleResize);
});

const handleResize = () => {
    if (showTooltip.value) {
        updateTooltipPosition();
    }
};
</script>

<style lang="scss" scoped>
.card-cursor {
    cursor: default;
    &:hover {
        background-color: unset;
    }
}
</style>
